import { CardSize } from '@enter/core';
import { PatientCollectionsChart } from '../chart/patient-collections-chart';
import _ from 'lodash';
import { IReactionDisposer, reaction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { Granularity } from '../../models';
import React from 'react';
import { Link } from 'react-router-dom';
import { IInjectedRootStore } from '../../stores';
import { Legend, timelineLabels } from '../chart';
import * as S from './patient-collections-card.styles';

interface IPatientCollectionsCardProps {
    children?: React.ReactNode;
    size?: CardSize;
}

interface IPatientCollectionsCardState {
    granularity: Granularity;
}

@inject('rootStore')
@observer
export class PatientCollectionsCard extends React.Component<
    IPatientCollectionsCardProps,
    IPatientCollectionsCardState
> {
    private disposer?: IReactionDisposer;

    constructor(props: IPatientCollectionsCardProps) {
        super(props);
        this.state = {
            granularity: Granularity.Monthly,
        };
    }
    public componentDidMount(): void {
        const rootStore = this.injected.rootStore;
        this.autoSelectGranularity(null);
        // NB: auto select only if date-range is changed externally
        this.disposer = reaction(
            () => rootStore.dateRangeStore.dateRange,
            this.autoSelectGranularity
        );
    }
    public componentWillUnmount(): void {
        if (this.disposer) {
            this.disposer();
        }
    }
    public render(): React.ReactElement {
        return (
            <S.PatientCollectionsCard
                size={this.props.size || 'medium'}
                title="Patient Collections"
                selectedGranularity={this.state.granularity}
                onGranularitySelect={this.changeGranularity}
                info={this.renderInfo()}
                Options={this.renderOptions()}
                Legend={this.renderLegend()}
                Link={this.renderLink()}>
                {this.renderChildren()}
            </S.PatientCollectionsCard>
        );
    }
    private get injected(): IInjectedRootStore {
        return this.props as IPatientCollectionsCardProps & IInjectedRootStore;
    }
    private readonly changeGranularity = (granularity: Granularity): void => {
        this.setState({
            granularity,
        });
    };
    private readonly autoSelectGranularity = (d: any): void => {
        const { dateRangeStore } = this.injected.rootStore;
        if (!dateRangeStore.hasGranularityForDateRange(this.state.granularity)) {
            this.changeGranularity(dateRangeStore.findGranularityForDateRange());
        }
    };

    private readonly exportData = (format: string): (() => void) => {
        return (): void => {
            window.alert(`TODO: Export PatientCollectionss data as ${format}`);
        };
    };
    private readonly renderInfo = (): React.ReactElement => {
        return (
            <span>
                Track patient payments to improve your revenue cycle. Make sure payments are transparent and smooth for
                your patients.
            </span>
        );
    };
    private readonly renderOptions = (): React.ReactElement | undefined => {
        return undefined;
    };
    private readonly renderLegend = (): React.ReactElement => {
        return (
            <Legend
                direction={this.props.size === 'large' ? 'vertical' : 'horizontal'}
                labels={timelineLabels.patientCollections}
            />
        );
    };
    private readonly renderLink = (): React.ReactElement => {
        return <Link to="/reports">Patient Collections Report</Link>;
    };
    private readonly renderChildren = (): React.ReactElement => {
        const { dateRangeStore, metricsStore } = this.injected.rootStore;
        const [startDate, endDate] = dateRangeStore.dateRange;

        if (_.isError(metricsStore.patientCollectionsTimeline)) {
            return <S.Message>No data is currently available.</S.Message>;
        } else if (dateRangeStore.hasGranularityForDateRange(this.state.granularity)) {
            return (
                <PatientCollectionsChart
                    granularity={this.state.granularity}
                    startDate={startDate}
                    endDate={endDate}
                    claimTypes={metricsStore.selectedClaimTypes}
                />
            );
        } else {
            return (
                <S.Message>
                    The current date range cannot be viewed with the {Granularity[this.state.granularity]} setting.
                </S.Message>
            );
        }
    };
}
